import { extractAmountFromInputString } from 'jigsaw';
import { Squash } from './types';
import { ConnectRequestParams, RecordWithPlaintext } from '@puzzlehq/sdk';
import debug from 'debug';
import { u128ToAscii } from '@puzzlehq/frontend-utils';
import _ from 'lodash';

export const log_history = debug('squash:history');
log_history.enabled = true;

export const levelKgs = [0, 10, 20, 30, 40, 50];

type GetSquashHistoryProps = {
  _squashRecords: RecordWithPlaintext[] | undefined;
  timeDeltaSeconds: number;
  startTimeUnix: number;
};

export const getSquashHistory = ({
  _squashRecords,
  timeDeltaSeconds,
  startTimeUnix,
}: GetSquashHistoryProps): Squash[] => {
  const time_delta_ms = timeDeltaSeconds * 1000;

  if (!_squashRecords) throw new Error('Squash Records undefined!');

  // Sort records by height to process them in chronological order
  const squashRecords = _squashRecords
    .slice()
    .sort((a, b) => a.height - b.height);

  if (squashRecords.length === 0) throw new Error('No Squash Records!');

  const squash_mint_kg = extractAmountFromInputString(
    squashRecords[0]?.data?.kg
  );
  const squash_mint_unix_s = extractAmountFromInputString(
    squashRecords[0]?.data?.last_water
  );

  const squash_id: string | undefined = squashRecords[0]?.data?.squash_id as
    | string
    | undefined;
  const squash_name = squashRecords[0]
    ? getSquashName(squashRecords[0])
    : undefined;

  if (squash_mint_kg === null || squash_mint_unix_s === null)
    throw new Error('squashMintRecord parsing error');

  if (squash_id === undefined) {
    throw new Error('squash_id is undefined!');
  } else if (squash_name === undefined) {
    throw new Error('squash_name is undefined!');
  }

  const squash_mint_unix_ms = squash_mint_unix_s * 1000;
  // Floor the timestamp to the nearest time delta interval
  const floored_squash_mint_unix_ms =
    Math.floor(squash_mint_unix_ms / time_delta_ms) * time_delta_ms;

  // Calculate number of time intervals, capped at 10,000 to prevent performance issues
  const n_time_deltas = Math.min(
    Math.floor(
      (startTimeUnix * 1000 - floored_squash_mint_unix_ms) / time_delta_ms
    ),
    10_000
  );

  const now = new Date();

  log_history('times', {
    squash_mint_unix_ms,
    squashGameMetadataStartTime: startTimeUnix * 1000,
    floored_squash_mint_unix_ms,
    n_time_deltas,
    now,
    nowUnix: now.getTime(),
  });

  let j = 0; // Index for squash records
  let prev_squash_kg: number | undefined = undefined;
  let prev_squash_level: number | undefined = undefined;
  let prev_squash_water_unix_ms: number | undefined = undefined;
  const squash_states: Squash[] = [];

  // Iterate through each time delta to build squash state history
  for (let i = 0; i <= n_time_deltas; i++) {
    const time_ms = floored_squash_mint_unix_ms + i * time_delta_ms;
    const next_squash_record: RecordWithPlaintext | undefined =
      squashRecords[j];

    j === 0 && console.log('next_squash_record', next_squash_record);

    const squash_water_kg = extractAmountFromInputString(
      next_squash_record?.data?.kg
    );
    const squash_level = extractAmountFromInputString(
      next_squash_record?.data?.level
    );

    const squash_water_unix_s = extractAmountFromInputString(
      next_squash_record?.data?.last_water
    );

    const squash_water_unix_ms = (squash_water_unix_s ?? 0) * 1000;
    const floored_squash_water_unix_ms =
      Math.floor(squash_water_unix_ms / time_delta_ms) * time_delta_ms;

    // If current time matches a watering event
    if (time_ms === floored_squash_water_unix_ms) {
      if (
        next_squash_record === undefined ||
        squash_water_kg === null ||
        squash_level === null ||
        squash_water_unix_s === null
      )
        throw new Error(
          `squashRecord parsing error, j: ${j}, ${next_squash_record}`
        );

      squash_states.push({
        kg: squash_water_kg / 1_000_000,
        mood: 'happy',
        lastWatered: new Date(squash_water_unix_ms),
        time: new Date(time_ms),
        onChainLevel: Math.max(squash_level, prev_squash_level ?? 0),
        displayedLevel: Math.floor(squash_water_kg / 1_000_000 / 10),
        squashId: squash_id,
        squashName: squash_name,
      });
      // Update previous state tracking
      prev_squash_kg = squash_water_kg / 1_000_000;
      prev_squash_level = squash_level;
      prev_squash_water_unix_ms = squash_water_unix_ms;
      j++;
    } else {
      if (
        prev_squash_water_unix_ms === undefined ||
        prev_squash_kg === undefined ||
        prev_squash_level === undefined
      )
        throw new Error(`previous squash data is undefined!`);

      // Handle states between watering events
      if (i === n_time_deltas && squash_states[i - 1].mood === 'happy') {
        // Set mood to thirsty at the end if previously happy
        squash_states.push({
          kg: squash_states[i - 1].kg,
          mood: 'thirsty',
          lastWatered: new Date(prev_squash_water_unix_ms),
          time: new Date(time_ms),
          onChainLevel: Math.max(squash_level ?? 0, prev_squash_level),
          displayedLevel: Math.floor(squash_states[i - 1].kg / 10),
          squashId: squash_id,
          squashName: squash_name,
        });
      } else {
        // Calculate water loss based on days missed
        const days_missed = Math.ceil(
          (time_ms - prev_squash_water_unix_ms) / time_delta_ms
        );

        // Reduce kg by half a unit per day, unless it's the final state
        const kg =
          i === n_time_deltas
            ? squash_states[i - 1].kg
            : Math.max(0, prev_squash_kg - days_missed / 2);

        // Add sad state for periods without water
        squash_states.push({
          kg,
          mood: i === 0 ? 'happy' : 'sad',
          lastWatered: new Date(prev_squash_water_unix_ms),
          time: new Date(time_ms),
          onChainLevel: Math.max(squash_level ?? 0, prev_squash_level),
          displayedLevel: Math.floor(kg / 10),
          squashId: squash_id,
          squashName: squash_name,
        });
      }
    }
  }

  return squash_states;
};

export const getTimeoutPromise = (
  timeoutMs: number,
  timeoutMessage: string
) => {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);
  });
};

export const connectRequest: ConnectRequestParams = {
  dAppInfo: {
    description: 'A relaxing farming game.',
    name: 'Squash Game',
  },
  permissions: {
    programIds: {
      [import.meta.env.VITE_NETWORK!]: [
        import.meta.env.VITE_SQUASH_PROGRAM_ID!,
        import.meta.env.VITE_COIN_CONTRACT!,
      ],
    },
  },
};

export const getInactiveSquashIds = (
  records?: RecordWithPlaintext[]
): string[] | undefined => {
  const ids = records
    ?.filter((record) => {
      const level = extractAmountFromInputString(record.data.level);
      return level !== null && level >= 5;
    })
    .map((record) => record.data.squash_id as string);

  return _.uniq(ids);
};

export const getActiveSquashIds = (
  records?: RecordWithPlaintext[]
): string[] | undefined => {
  return records
    ?.filter((record) => {
      const level = extractAmountFromInputString(record.data.level);
      return level !== null && level < 5;
    })
    .map((record) => record.data.level as string);
};

export const getSquashName = (record: RecordWithPlaintext) => {
  const u128str = record.data.squash_name;
  if (u128str && typeof u128str === 'string') {
    const u128 = u128str.replace('.private', '');
    const name = u128ToAscii(u128);
    return name;
  }
};

export const SQUASH_AUDIO_BASE_URL =
  'https://storage.googleapis.com/bucket-quickstart_jigsaw-bigshot/Squash/audio';
